// CreateSpecialty.tsx
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  createSpecialty,
  getAllSpecialties,
  getSpecialtyByID,
  specialtyImageUpload,
  updateSpecialty,
} from "./services/specialties.service";
import { clearSpecialtyDetails } from "./specialtySlice";
import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";
import { getFile } from "../../_helper/helper";
import FormTextArea from "../../components/formFields/FormTextArea";

const CreateSpecialty: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const SpecialtyID = useAppSelector((state) => state.drawer.data);
  const SpecialtyDetail = useAppSelector(
    (state) => state.specialty.SpecialtyDetail,
  );
  const loading = useAppSelector((state) => state.specialty.formLoading);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [SpecialtyDetail]);

  useEffect(() => {
    if (SpecialtyID) {
      dispatch(getSpecialtyByID(SpecialtyID));
    }
    return () => {
      dispatch(clearSpecialtyDetails());
    };
  }, [SpecialtyID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values.Title ?? "",
      Remarks: values.Remarks ?? "",
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const Image =
      values?.Image && typeof values?.Image !== "string"
        ? getFile(values?.Image[0])
        : "";

    try {
      if (SpecialtyID) {
        const editPayload = {
          ...formData,
          ID: SpecialtyID,
        };

        const response = await dispatch(updateSpecialty(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());

          if (values?.Image && typeof values?.Image !== "string") {
            ImageData.append("UploadImage", Image);
            ImageData.append("ID", SpecialtyID);
            await dispatch(specialtyImageUpload(ImageData));
          }
          dispatch(getAllSpecialties());
          dispatch(clearSpecialtyDetails());
        }
      } else {
        const response = await dispatch(createSpecialty(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          if (
            values?.Image &&
            typeof values?.Image !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("UploadImage", Image);
            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(specialtyImageUpload(ImageData));
          }
          dispatch(getAllSpecialties());
          dispatch(clearSpecialtyDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={SpecialtyDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
                <GridLayoutItem className="master-drawer-header">
                  <Typography.h4 className="m-0">
                    {SpecialtyID ? "Update Speciality" : "Add Speciality"}
                  </Typography.h4>
                  <IconButton
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                    icon={RxCross1}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Title"
                    label="Speciality Title"
                    maxLength="100"
                    placeholder="Enter Speciality title"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Remarks"
                    label="Remarks"
                    maxLength="500"
                    placeholder="Enter remarks"
                    component={FormTextArea}
                    style={{ height: 120 }}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    justifyContent="start"
                    name="Image"
                    label="Speciality Image"
                    type="file"
                    alternateName="Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="IsActive"
                    label="Status"
                    placeholder="Select status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: "20px",
                    textAlign: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={SpecialtyID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateSpecialty;
